import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type Highcharts from "highcharts/es-modules/masters/highcharts.src.js";
import { Chart } from "@highcharts/react";
import { AMCReportTable } from "./AMCReportTable";
import { metricColors } from "../../../components/custom/analytics/palettes";
import { token } from "../../../../styled-system/tokens";
import { time_to_conversion_metrics, type TimeToConversionMetrics } from "../utils/utils";

interface ConversionTimeDataItem {
  time_to_conversion: TimeToConversionMetrics;
  campaign: string;
  total_brand_purchases: number;
  purchases: number;
}

interface Props {
  conversionData: ConversionTimeDataItem[];
}

const ConversionHistogram = ({ conversionData }: Props) => {
  const { t } = useTranslation(["amc"]);
  const chartOptions = useMemo((): Highcharts.Options => {
    const timeBucketTotals: Record<string, number> = {};

    conversionData.forEach(({ time_to_conversion, purchases }) => {
      timeBucketTotals[time_to_conversion] = (timeBucketTotals[time_to_conversion] || 0) + purchases;
    });

    const sortedTimeBuckets = time_to_conversion_metrics.filter((bucket) => Object.hasOwn(timeBucketTotals, bucket));

    return {
      chart: {
        type: "column",
        height: 400,
        style: {
          fontSize: token("fontSizes.chart"),
          fontFamily: "Poppins",
          fontWeight: "300",
        },
      },
      title: {
        text: "",
      },
      xAxis: {
        categories: sortedTimeBuckets,
        title: {
          text: t(($) => $.amc["Time to Conversion"]),
        },
      },
      yAxis: {
        title: {
          text: t(($) => $.amc["Total Purchases"]),
        },
        min: 0,
      },
      tooltip: {
        shared: true,
        valueSuffix: t(($) => $.amc[" purchases"]),
      },
      plotOptions: {
        column: {
          stacking: "normal",
        },
      },
      series: [
        {
          type: "column",
          name: t(($) => $.amc["Total Purchases"]),
          data: sortedTimeBuckets.map((bucket) => timeBucketTotals[bucket] || 0),
          color: metricColors.units[0],
        },
      ],
      credits: {
        enabled: false,
      },
    };
  }, [conversionData, t]);

  return <Chart options={chartOptions} />;
};

interface ConversionTimeProps {
  reportData: ConversionTimeDataItem[];
  reportDetails: ConversionTimeDataItem[];
}
type PivotedRow = {
  campaign: string;
} & {
  [key in TimeToConversionMetrics]?: number;
};

function pivotConversionData(data: ConversionTimeDataItem[]): PivotedRow[] {
  const campaignMap: Record<string, PivotedRow> = {};

  data.forEach(({ campaign, time_to_conversion, purchases }) => {
    if (!(campaign in campaignMap)) {
      campaignMap[campaign] = { campaign };
    }

    const campaignRow = campaignMap[campaign]!;
    const key = time_to_conversion;
    const prev = campaignRow[key];

    campaignRow[key] = (typeof prev === "number" ? prev : 0) + purchases;
  });

  return Object.values(campaignMap);
}

const AMCTimeToConversion = ({ reportData, reportDetails }: ConversionTimeProps) => {
  const pivotedData = useMemo(() => pivotConversionData(reportData), [reportData]);
  return (
    <>
      <ConversionHistogram conversionData={reportData} />

      <AMCReportTable reportData={pivotedData} reportDetails={reportDetails} />
    </>
  );
};

export default AMCTimeToConversion;
